import { parseISO, isFuture, isPast } from 'date-fns';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IonItem,
  IonList,
  IonButton,
  IonLabel,
  IonNote,
  IonBadge,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
} from '@ionic/react';

import { getCoupons, saveCoupon } from '@/actions/coupons';
import { couponsSelector } from '@/selectors/coupons';
import useResourceLoader from '@/hooks/useResourceLoader';
import { presentAsLine, inDollars, formatDateShort } from '@/helpers/humanize';
import { requireRole } from '@/helpers/permissions';
import LoadingPage from '@/pages/LoadingPage';
import Page from '@/pages/Page';

export default requireRole(['owner', 'manager'], CouponsPage);

function CouponsPage() {
  const dispatch = useDispatch();
  const coupons = useSelector(couponsSelector);
  const initialLoading = useResourceLoader(getCoupons());
  const listRef = useRef();

  if (initialLoading) return <LoadingPage />;

  const activate = (coupon, active) => async (e) => {
    await dispatch(saveCoupon({id: coupon.id, active}));
    listRef.current?.closeSlidingItems();
  };

  return (
    <Page title="Coupons" primaryButton={<IonButton routerLink="/coupons/new">New</IonButton>}>
      <IonList ref={listRef}>
        {coupons?.map((coupon) => (
          <IonItemSliding key={coupon.id}>
            <IonItem routerLink={`/coupons/${coupon.id}/edit`}>
              <IonLabel>
                <h2>
                  {coupon.code}
                  <AvailablityStatusBadge coupon={coupon}/>
                </h2>
                <p>{presentAsLine(
                  coupon.amount_cents && inDollars(coupon.amount_cents),
                  coupon.amount_percent && `${coupon.amount_percent}%`,
                  presentDateRange(coupon)
                )}</p>
              </IonLabel>
              <IonNote slot="end">{coupon.uses ?? 0}/{coupon.limit_total_uses ?? '∞'}</IonNote>
            </IonItem>
            <IonItemOptions>
              {coupon.active ? (
                <IonItemOption color="danger" onClick={activate(coupon, false)}>Deactivate</IonItemOption>
              ) : (
                <IonItemOption color="secondary" onClick={activate(coupon, true)}>Reactivate</IonItemOption>
              )}
            </IonItemOptions>
          </IonItemSliding>
        ))}
      </IonList>
      {_.isEmpty(coupons) && (
        <div className="ion-padding ion-text-center">
          <br/><br/><br/>
          <IonButton color="primary" routerLink="/coupons/new">Create your first coupon</IonButton>
        </div>
      )}
    </Page>
  );
}

function presentDateRange({start_date, end_date}) {
  if (!start_date && !end_date) return;
  if (start_date && end_date) return [start_date, end_date].map(formatDateShort).join('–');
  if (start_date) return `Activates ${formatDateShort(start_date)}`;
  if (end_date) return `Expires ${formatDateShort(end_date)}`;
}

function availablity({active, start_date, end_date, limit_total_uses, uses}) {
  if (!active) return {available: false, color: 'medium', description: 'inactive'};
  if (start_date && isFuture(parseISO(start_date))) return {available: false, color: 'light', description: 'upcoming'};
  if (end_date && isPast(parseISO(end_date))) return {available: false, color: 'dark', description: 'expired'};
  if (limit_total_uses > 0 && uses >= limit_total_uses) return {available: false, color: 'danger', description: 'used up'};
  if (limit_total_uses > 0 && uses >= limit_total_uses * 0.9) return { available: true, color: 'warning', description: 'near limit' };
  return {available: true, color: 'success', description: 'available now'};
}

function AvailablityStatusBadge({coupon}) {
  const {color, description} = availablity(coupon);
  return <IonBadge color={color} className="ion-margin-start">{description}</IonBadge>
}
